import { useEffect, useMemo, useRef, useState } from 'react';
import { formatBytes, formatIstTime, istDayKey, unfilledPlaceholders } from '@wa/shared';
import { QuickReplyPanel, type QuickReplyAttachment, type QuickReplyKeys } from '@/components/QuickReplyPanel';
import { SchedulePanel } from '@/components/SchedulePanel';
import { QuoteBlock } from '@/components/QuoteBlock';
import type { Message, ScheduledMessage } from '@wa/shared';

interface Props {
  /** Resolves {{name}} in a quick reply. */
  contactName: string | null;
  /** For a quick reply's {{field.key}}. */
  contactId?: number | null;
  isSending: boolean;
  isUploading: boolean;
  /** Resolves when sent; rejects when not, and the composer puts the text back. */
  onSend: (body: string) => Promise<unknown> | void;
  /** The same send with a date on it — see the F16 section of the build reference. */
  onSchedule: (body: string, whenUtcIso: string) => void;
  onCancelScheduled: (id: number) => void;
  scheduled: ScheduledMessage[];
  windowExpiresAt: string | null;
  onSendMedia: (file: File, caption: string) => Promise<unknown> | void;
  /** A file already uploaded — a quick reply's attachment — by asset id. */
  onSendStoredMedia?: (mediaAssetId: number, caption: string) => Promise<unknown> | void;
  onOpenTemplates: () => void;
  /** Opens the product picker — the catalogue into the chat. */
  onOpenProducts: () => void;
  /** The message being replied to, shown above the box until sent or dismissed. */
  replyTo?: Message | null;
  onCancelReply?: () => void;
}

/** WhatsApp will not accept more than this, so the browser refuses it first. */
const MAX_BYTES = 16 * 1024 * 1024;

/**
 * Per send. The Cloud API has no album message — every file is its own message,
 * its own outbox row and its own receipt — so this is a run of separate sends,
 * and a run of thirty from a business number is what gets a number reported.
 * WhatsApp's own picker stops at thirty; ten is plenty for a support reply.
 */
const MAX_FILES = 10;

export function Composer({
  contactName, contactId, isSending, isUploading, onSend, onSendMedia, onSendStoredMedia, onOpenTemplates, onOpenProducts,
  onSchedule, onCancelScheduled, scheduled, windowExpiresAt, replyTo, onCancelReply,
}: Props) {
  const [showSchedule, setShowSchedule] = useState(false);
  const [draft, setDraft] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  // A quick reply's file: already at Meta, referenced by id, sent with the text
  // as its caption. Kept apart from `attachments` because nothing is uploaded.
  const [stored, setStored] = useState<QuickReplyAttachment | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [quickRepliesOff, setQuickRepliesOff] = useState(false);
  const picker = useRef<HTMLInputElement>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  const quickReplies = useRef<QuickReplyKeys>(null);

  // "Reply" on a bubble means "I am about to type", so the caret goes to the box.
  useEffect(() => { if (replyTo) box.current?.focus(); }, [replyTo]);

  // The box grows with the text, upward (the composer is pinned to the bottom),
  // to a ceiling of about twelve lines, then scrolls. A manual resize handle
  // was the alternative and nobody found it.
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 288)}px`;
  }, [draft]);

  /**
   * The panel opens only when the slash is the very first character.
   *
   * Firing on any slash would open it in the middle of every URL an agent pastes
   * — "https://" alone would trigger it twice. A quick reply is the whole
   * message rather than a fragment of one, so the first character is where it
   * belongs.
   */
  const quickReplyQuery = useMemo(() => {
    if (quickRepliesOff || attachments.length > 0 || stored) return null;
    if (!draft.startsWith('/')) return null;

    const upto = draft.slice(1);
    // A newline means they have moved on and meant a literal slash.
    return upto.includes('\n') ? null : upto;
  }, [draft, quickRepliesOff, attachments.length, stored]);

  // Placeholders the quick reply could not fill; the agent types those.
  const unfilled = useMemo(() => unfilledPlaceholders(draft), [draft]);

  // True for the whole run of a multi-file send. The parent's isUploading drops
  // between files, and the box must not reopen in that gap.
  const [inRun, setInRun] = useState(false);
  const busy = isSending || isUploading || inRun;
  const [dragging, setDragging] = useState(false);

  /**
   * A screenshot on the clipboard arrives as a File called "image.png" with no
   * date; a chip saying "image.png" three times is unreadable, and the name
   * travels to the customer on a document send. Named by the moment it was pasted.
   */
  function fromClipboard(items: DataTransfer | null): File[] {
    if (!items) return [];
    const files = Array.from(items.files);
    if (files.length === 0) return [];
    const stamp = new Date().toISOString().slice(0, 16).replace('T', ' ').replace(':', '.');
    return files.map((file, i) => {
      if (file.name && file.name !== 'image.png' && file.name !== 'image.jpeg') return file;
      const ext = file.type === 'image/jpeg' ? 'jpg' : 'png';
      return new File([file], `Screenshot ${stamp}${files.length > 1 ? ` (${i + 1})` : ''}.${ext}`, { type: file.type });
    });
  }

  function onPaste(event: React.ClipboardEvent) {
    // Only when something pasted is a file: text keeps the browser's own paste.
    // Pasting a picture used to drop the literal name "image.png" into the draft
    // and nothing else — the one thing WhatsApp Desktop and every other inbox
    // get right, and the first thing the new client tried.
    const files = fromClipboard(event.clipboardData);
    if (files.length === 0 || busy) return;
    event.preventDefault();
    choose(files);
  }

  function onDrop(event: React.DragEvent) {
    event.preventDefault();
    setDragging(false);
    if (busy) return;
    choose(fromClipboard(event.dataTransfer));
  }

  function choose(picked: FileList | File[] | null) {
    if (!picked || picked.length === 0) return;

    // Rejected here rather than after a slow upload that was always going to fail.
    const tooBig = Array.from(picked).filter((f) => f.size > MAX_BYTES);
    const fits = Array.from(picked).filter((f) => f.size <= MAX_BYTES);

    const problems: string[] = [];
    if (tooBig.length > 0) {
      problems.push(`${tooBig.map((f) => `${f.name} (${formatBytes(f.size)})`).join(', ')} left out — WhatsApp accepts up to 16 MB each.`);
    }

    setAttachments((current) => {
      const room = MAX_FILES - current.length;
      if (fits.length > room) {
        problems.push(`Up to ${MAX_FILES} files in one go; the first ${Math.max(room, 0)} were kept.`);
      }
      return [...current, ...fits.slice(0, Math.max(room, 0))];
    });

    setError(problems.length > 0 ? problems.join(' ') : null);
  }

  function insertQuickReply(text: string, attachment: QuickReplyAttachment | null) {
    setDraft(text);
    setQuickRepliesOff(true);
    if (attachment) { setStored(attachment); setAttachments([]); }

    // The caret goes to the first placeholder left unfilled, selected, so the
    // agent's next keystroke replaces it; otherwise to the end.
    queueMicrotask(() => {
      box.current?.focus();
      const open = text.search(/\{\{\s*[a-zA-Z_][\w.-]*\s*\}\}/);
      if (open >= 0) {
        const close = text.indexOf('}}', open) + 2;
        box.current?.setSelectionRange(open, close);
      } else {
        box.current?.setSelectionRange(text.length, text.length);
      }
    });
  }

  function submit() {
    if (busy) return;

    // A placeholder still in the text is a message that reads "{{order_no}}" to
    // the customer. Refused here, with the box focused on it, rather than sent.
    if (unfilled.length > 0) {
      setError(`Fill in {{${unfilled[0]}}} before sending.`);
      const open = draft.search(/\{\{/);
      box.current?.focus();
      box.current?.setSelectionRange(open, draft.indexOf('}}', open) + 2);
      return;
    }

    if (stored && onSendStoredMedia) {
      const file = stored;
      const caption = draft.trim();
      setStored(null);
      setDraft('');
      Promise.resolve(onSendStoredMedia(file.mediaAssetId, caption)).catch(() => {
        setStored((current) => current ?? file);
        setDraft((current) => (current === '' ? caption : current));
      });
      return;
    }

    // Cleared at once, as a chat app does, and restored if the send fails and
    // the agent has not started something new — a 402 or a closed window used
    // to cost them the paragraph they had just written.
    if (attachments.length > 0) {
      const files = attachments;
      const caption = draft.trim();
      setAttachments([]);
      setDraft('');

      // One message per file, in the order they were picked, each sent only
      // once the previous one was accepted: the outbox sends a number's rows in
      // id order, so what the customer sees is the order the agent chose. The
      // caption rides on the first, as WhatsApp does. A rejection part-way
      // through puts back the file that failed and everything after it, not the
      // ones already queued.
      setInRun(true);
      void (async () => {
        try {
          for (let i = 0; i < files.length; i++) {
            const file = files[i];
            if (!file) continue;
            try {
              await onSendMedia(file, i === 0 ? caption : '');
            } catch {
              const rest = files.slice(i);
              setAttachments((current) => (current.length === 0 ? rest : current));
              if (i === 0) setDraft((current) => (current === '' ? caption : current));
              return;
            }
          }
        } finally {
          setInRun(false);
        }
      })();
      return;
    }

    const body = draft.trim();
    if (!body) return;
    setDraft('');
    setQuickRepliesOff(false);
    Promise.resolve(onSend(body)).catch(() => {
      setDraft((current) => (current === '' ? body : current));
    });
  }

  // The closed-window state lives in SessionOpener now, one level up: the thread
  // renders that INSTEAD of this composer rather than this composer rendering a
  // banner above itself. A free-form box behind a notice saying free-form is
  // refused is a box that exists to be typed into and then rejected.

  return (
    <div
      className={`relative flex w-full flex-col gap-2 rounded-xl transition-shadow ${dragging ? 'ring-2 ring-(--color-brand) ring-offset-2' : ''}`}
      onDragOver={(event) => {
        if (!event.dataTransfer.types.includes('Files')) return;
        event.preventDefault();
        if (!dragging) setDragging(true);
      }}
      onDragLeave={(event) => {
        // Leaving a child fires too; only the real exit clears the ring.
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false);
      }}
      onDrop={onDrop}
    >

      {/* What is waiting, as one line; the panel itself only when asked for.
          It used to stay open for as long as anything was scheduled, which on
          a chat with one message queued meant a permanent box above the reply. */}
      {!showSchedule && scheduled.length > 0 && (
        <button
          type="button"
          onClick={() => setShowSchedule(true)}
          className="tap flex items-center gap-2 self-start rounded-full border border-(--color-line) bg-(--color-surface) py-1 pr-3 pl-2 text-small text-(--color-muted) hover:text-(--color-ink)"
        >
          <svg viewBox="0 0 20 20" width="15" height="15" aria-hidden="true" className="text-(--color-warn)">
            <circle cx="10" cy="10" r="7.2" fill="none" stroke="currentColor" strokeWidth="1.6" />
            <path d="M10 6v4.3l2.8 1.7" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span>
            {scheduled.length === 1 ? '1 message' : `${scheduled.length} messages`} scheduled · next {nextScheduledLabel(scheduled)}
          </span>
          <span className="text-(--color-brand-strong)">View</span>
        </button>
      )}

      {showSchedule && (
        <SchedulePanel
          windowExpiresAt={windowExpiresAt}
          pending={scheduled}
          isBusy={busy}
          onSchedule={(when) => {
            const body = draft.trim();
            if (!body) return;
            onSchedule(body, when);
            setDraft('');
            setShowSchedule(false);
          }}
          onCancel={onCancelScheduled}
          onClose={() => setShowSchedule(false)}
        />
      )}

      <QuickReplyPanel
        ref={quickReplies}
        query={quickReplyQuery}
        contactName={contactName}
        contactId={contactId}
        draft={draft}
        onPick={insertQuickReply}
        onClose={() => setQuickRepliesOff(true)}
      />

      {error && (
        <p role="alert" className="rounded border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">
          {error}
        </p>
      )}

      {/* Replying to a specific message. The same block the bubble draws, with a
          ✕, so what the agent sees here is what the customer's phone will show
          above the reply. */}
      {replyTo && onCancelReply && (
        <div className="rounded-lg border border-(--color-line) bg-(--color-sunken) px-2 pt-1">
          <QuoteBlock
            quote={{
              id: replyTo.id,
              direction: replyTo.direction,
              type: replyTo.messageType,
              body: replyTo.body,
              agentName: replyTo.sentByAgentName,
              fileName: replyTo.mediaFileName,
              unavailable: false,
            }}
            contactName={contactName}
            outbound={false}
            onDismiss={onCancelReply}
          />
        </div>
      )}

      {stored && (
        <div className="flex items-center gap-2 rounded-lg border border-(--color-line) bg-(--color-sunken) px-3 py-2">
          <span className="min-w-0 flex-1">
            <span className="block truncate text-small font-medium">{stored.fileName}</span>
            <span className="block text-micro text-(--color-muted)">From the quick reply — sent with the text as its caption</span>
          </span>
          <button
            type="button"
            onClick={() => setStored(null)}
            disabled={busy}
            className="tap grid shrink-0 place-items-center rounded px-2 text-(--color-muted) hover:text-(--color-ink) disabled:opacity-50"
            aria-label="Remove attachment"
          >
            <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
              <path d="M5 5l10 10M15 5L5 15" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      )}

      {attachments.length > 0 && (
        <div className="rounded-lg border border-(--color-line) bg-(--color-sunken) px-3 py-2">
          <ul className="flex flex-wrap gap-2" aria-label="Attachments">
            {attachments.map((file, index) => (
              <AttachmentChip
                key={`${file.name}-${file.size}-${index}`}
                file={file}
                disabled={busy}
                onRemove={() => setAttachments((current) => current.filter((_, i) => i !== index))}
              />
            ))}
          </ul>
          {attachments.length > 1 && (
            <p className="mt-1.5 text-micro text-(--color-muted)">
              {attachments.length} files — sent as {attachments.length} messages, in this order. The caption goes on the first.
            </p>
          )}
        </div>
      )}

      {/* One box: the text on top, full width, growing upward as it is typed;
          the tools and Send in a thin row under it. The four tools used to sit
          beside the text and took a third of the width on a laptop. */}
      <div
        className={[
          'group/box rounded-xl border bg-(--color-surface) shadow-[0_1px_2px_rgba(0,0,0,0.06)] transition-[border-color,box-shadow]',
          'border-(--color-line) focus-within:border-(--color-brand) focus-within:shadow-[0_0_0_3px_color-mix(in_srgb,var(--color-brand)_14%,transparent)]',
          busy ? 'opacity-70' : '',
        ].join(' ')}
      >
        <input
          ref={picker}
          type="file"
          multiple
          className="hidden"
          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
          onChange={(event) => {
            choose(event.target.files);
            event.target.value = '';
          }}
        />

        <textarea
          ref={box}
          rows={1}
          value={draft}
          onPaste={onPaste}
          onChange={(event) => {
            setDraft(event.target.value);
            // Clearing the box re-arms the panel, so a second slash works.
            if (!event.target.value.startsWith('/')) setQuickRepliesOff(false);
          }}
          onKeyDown={(event) => {
            // Escape closes the panel and leaves the draft alone: someone who
            // typed a literal slash must not have to delete anything.
            if (event.key === 'Escape' && quickReplyQuery !== null) {
              event.preventDefault();
              setQuickRepliesOff(true);
              return;
            }

            // While the panel is open the arrows move its highlight and Enter
            // picks it. Before this, Enter with "/gree" on screen and "greeting"
            // drawn as chosen sent the literal "/gree" to the customer.
            if (quickReplyQuery !== null) {
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                quickReplies.current?.move(event.key === 'ArrowDown' ? 1 : -1);
                return;
              }
              if (event.key === 'Enter' && !event.shiftKey && quickReplies.current?.pick()) {
                event.preventDefault();
                return;
              }
            }

            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              submit();
            }
          }}
          placeholder={attachments.length > 1 ? 'Caption for the first file (optional)' : attachments.length === 1 || stored ? 'Add a caption (optional)' : 'Write a reply — / for quick replies'}
          disabled={busy}
          aria-label="Reply"
          // min-height opens to three lines when the pointer or the caret is in
          // the box, so it reads as a writing area and not a search field; the
          // height itself follows the text (see the effect above).
          className="block w-full min-h-10 resize-none bg-transparent px-3.5 pt-2.5 pb-0.5 text-body leading-6 outline-none focus-visible:outline-none transition-[min-height] duration-150 placeholder:text-(--color-faint) group-hover/box:min-h-[4.5rem] focus:min-h-[4.5rem] disabled:opacity-60"
        />

        <div className="flex items-center gap-0.5 px-1.5 pb-1.5">
          <ToolButton label="Attach a file" onClick={() => picker.current?.click()} disabled={busy}>
            <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
              <path d="M13.5 6.5 8 12a2.1 2.1 0 0 0 3 3l5.5-5.5a4.2 4.2 0 0 0-6-6L5 9a6.3 6.3 0 0 0 9 9l4-4"
                    fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </ToolButton>

          <ToolButton label="Send a template" onClick={onOpenTemplates} disabled={busy}>
            <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
              <rect x="3" y="4" width="14" height="12" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
              <path d="M3 8h14M7 11.5h6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </ToolButton>

          <ToolButton label="Send products from the catalogue" onClick={onOpenProducts} disabled={busy}>
            <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
              <path d="M4 7h12l-1 9H5z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
              <path d="M7.5 7V5.5a2.5 2.5 0 0 1 5 0V7" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </ToolButton>

          {/* Only offered once there is something to schedule. An empty draft with a
              date on it is not a message, and the button would be a dead end. */}
          <ToolButton
            label="Send later"
            onClick={() => setShowSchedule((v) => !v)}
            disabled={busy || (!draft.trim() && !showSchedule)}
            pressed={showSchedule}
          >
            <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
              <circle cx="10" cy="10" r="7.2" fill="none" stroke="currentColor" strokeWidth="1.5" />
              <path d="M10 6v4.3l2.8 1.7" fill="none" stroke="currentColor" strokeWidth="1.5"
                    strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </ToolButton>

          <span className="flex-1" />

          <span className="hidden text-micro text-(--color-faint) sm:inline" aria-hidden="true">
            Enter to send · Shift+Enter for a new line
          </span>

          <button
            type="button"
            onClick={submit}
            disabled={busy || (!draft.trim() && attachments.length === 0 && !stored)}
            className="tap ml-2 grid shrink-0 place-items-center rounded-lg bg-(--color-brand) px-4 py-1.5 text-body font-medium text-(--color-brand-ink) disabled:opacity-40"
          >
            {isUploading ? 'Uploading…' : isSending ? '…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** One of the tools under the box: icon only, the label on hover and for a reader. */
function ToolButton({ label, onClick, disabled, pressed, children }: {
  label: string; onClick: () => void; disabled?: boolean; pressed?: boolean; children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      aria-pressed={pressed}
      className={[
        'tap grid size-8 place-items-center rounded-md text-(--color-muted) transition hover:bg-(--color-sunken) hover:text-(--color-ink) disabled:opacity-40 disabled:hover:bg-transparent',
        pressed ? 'bg-(--color-sunken) text-(--color-brand)' : '',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

/**
 * A picked file before it is sent. A picture shows as a picture — the thing
 * the agent wants to confirm is "is this the right screenshot", and a name
 * like "Screenshot 2026-10-05 12.18.png" does not answer that. Anything else
 * shows its kind, name and size.
 */
function AttachmentChip({ file, disabled, onRemove }: { file: File; disabled: boolean; onRemove: () => void }) {
  const isImage = file.type.startsWith('image/');
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!isImage) return;
    const next = URL.createObjectURL(file);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [file, isImage]);

  const remove = (
    <button
      type="button"
      onClick={onRemove}
      disabled={disabled}
      className="tap absolute -top-1.5 -right-1.5 grid size-5 place-items-center rounded-full border border-(--color-line) bg-(--color-surface) text-(--color-muted) shadow-sm hover:text-(--color-bad) disabled:opacity-50"
      aria-label={`Remove ${file.name}`}
    >
      <svg viewBox="0 0 20 20" width="11" height="11" aria-hidden="true">
        <path d="M5 5l10 10M15 5L5 15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </button>
  );

  if (isImage && url) {
    return (
      <li className="relative" title={`${file.name} · ${formatBytes(file.size)}`}>
        <img src={url} alt={file.name} className="size-20 rounded-md border border-(--color-line) object-cover" />
        {remove}
      </li>
    );
  }

  const ext = (file.name.split('.').pop() ?? '').toUpperCase().slice(0, 4);
  return (
    <li className="relative flex max-w-60 items-center gap-2 rounded-md border border-(--color-line) bg-(--color-surface) py-1.5 pr-3 pl-2">
      <span className="grid size-9 shrink-0 place-items-center rounded bg-(--color-sunken) text-micro font-semibold text-(--color-muted)" aria-hidden="true">
        {ext || 'FILE'}
      </span>
      <span className="min-w-0">
        <span className="block truncate text-small font-medium">{file.name}</span>
        <span className="block text-micro text-(--color-muted)">{formatBytes(file.size)}</span>
      </span>
      {remove}
    </li>
  );
}

/** "12:44 pm" today, "tomorrow 9:00 am" otherwise — for the one-line strip. */
function nextScheduledLabel(items: ScheduledMessage[]): string {
  const next = [...items].sort((a, b) => a.scheduledFor.localeCompare(b.scheduledFor))[0];
  if (!next) return '';
  const today = istDayKey(new Date().toISOString());
  const tomorrow = istDayKey(new Date(Date.now() + 86_400_000).toISOString());
  const day = istDayKey(next.scheduledFor);
  const time = formatIstTime(next.scheduledFor);
  return day === today ? time : day === tomorrow ? `tomorrow ${time}` : `${day} ${time}`;
}
